import { createFileRoute, Link, useBlocker, useRouter } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Code2,
  Eye,
  Loader2,
  Mail,
  Maximize,
  Minimize,
  Plus,
  Save,
  Search,
  Send,
  Trash2,
  UserRound,
  Users,
  Upload,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { getOrCreateCampaignAlias } from "@/lib/campaign-alias.functions";
import { minimizePixelSnippet } from "@/lib/pixel-snippet";
import { listSmtpAccounts, sendSmtpMail } from "@/lib/smtp-accounts.functions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/_authenticated/campaign/$campaignId")({
  component: CampaignDetailPage,
  head: () => ({ meta: [{ title: "Kampagne bearbeiten — InboxPulse" }] }),
  errorComponent: CampaignDetailError,
  notFoundComponent: CampaignDetailNotFound,
});

type Recipient = {
  id: string;
  email: string;
  fields: Record<string, string>;
  position: number;
};

type ListItem = { id: string; value: string; position: number };

const MAX_SUBJECTS = 10000;
const MAX_SENDERS = 10000;
const MAX_RECIPIENTS_PER_CSV = 50000;
const RECIPIENT_PAGE_SIZE = 100;

// Produktive Custom-Domain. deliverability-delight.lovable.app antwortet auf
// /a/<token>.png mit HTTP 302 → meineasykredit.click. Gmail/Outlook-Image-
// Proxies folgen bei <img>-Tags KEINEN 302-Redirects → kein Open-Tracking.
// Mit der Custom-Domain liefert der Server direkt 200 OK + image/png.
const DEFAULT_TRACKING_ORIGIN = "https://meineasykredit.click";
const TRACKING_SNIPPET_START = "<!-- TRACKING-SNIPPET: START (automatisch eingefügt) -->";
const TRACKING_SNIPPET_END = "<!-- TRACKING-SNIPPET: ENDE -->";

function injectTrackingSnippet(html: string, pixelUrl: string): string {
  if (!pixelUrl || !html.trim()) return html;
  const markedSnippetPattern =
    /<!-- TRACKING-SNIPPET: START \(automatisch eingefügt\) -->[\s\S]*?<!-- TRACKING-SNIPPET: ENDE -->\s*/gi;
  const trackingImagePattern =
    /<img\b[^>]*\bsrc=["'][^"']*\/(?:a\/[a-z0-9]{8}\.png|api\/public\/pixel\/)[^"']*["'][^>]*>\s*/gi;
  const cleanedHtml = html
    .replace(markedSnippetPattern, "")
    .replace(trackingImagePattern, "")
    .trimEnd();
  // Minimaler <img>-Tag (nur src + CSS-Size, keine width/height-Attribute,
  // keine Tracking-Schlagwörter). Single source of truth: minimizePixelSnippet.
  const trackingImage = minimizePixelSnippet(pixelUrl).snippet;
  const markedSnippet = `${TRACKING_SNIPPET_START}\n${trackingImage}\n${TRACKING_SNIPPET_END}`;

  if (/<\/body\s*>/i.test(cleanedHtml)) {
    return cleanedHtml.replace(/<\/body\s*>/i, `${markedSnippet}\n</body>`);
  }
  return `${cleanedHtml}\n${markedSnippet}`;
}

function splitCsvRow(row: string, delimiter: string): string[] {
  const cells: string[] = [];
  let current = "";
  let quoted = false;
  for (let index = 0; index < row.length; index += 1) {
    const character = row[index];
    if (character === '"' && quoted && row[index + 1] === '"') {
      current += '"';
      index += 1;
    } else if (character === '"') {
      quoted = !quoted;
    } else if (character === delimiter && !quoted) {
      cells.push(current.trim());
      current = "";
    } else {
      current += character;
    }
  }
  cells.push(current.trim());
  return cells;
}

function normalizeField(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

type RecipientParseResult = {
  rows: Array<{ email: string; fields: Record<string, string> }>;
  totalLines: number;
  empty: number;
  invalid: number;
  duplicates: number;
};

function parseRecipientsCsv(text: string): RecipientParseResult {
  const rawLines = text.split(/\r?\n/);
  const lines: string[] = [];
  let empty = 0;
  for (const line of rawLines) {
    if (line.trim()) lines.push(line);
    else empty += 1;
  }
  if (!lines.length)
    return { rows: [], totalLines: rawLines.length, empty, invalid: 0, duplicates: 0 };
  const firstLine = lines[0] ?? "";
  const delimiter =
    [",", ";", "\t"].sort(
      (left, right) => firstLine.split(right).length - firstLine.split(left).length,
    )[0] ?? ",";
  const firstCells = splitCsvRow(firstLine, delimiter);
  const normalized = firstCells.map(normalizeField);
  const aliases = ["email", "e_mail", "recipient", "recipient_email", "lead_email"];
  const foundEmailIndex = normalized.findIndex((field) => aliases.includes(field));
  const hasHeader = foundEmailIndex >= 0;
  const emailIndex = hasHeader ? foundEmailIndex : 0;
  const headers = hasHeader
    ? normalized.map((field, index) => field || `spalte_${index + 1}`)
    : ["email"];
  const unique = new Map<string, { email: string; fields: Record<string, string> }>();
  let invalid = 0;
  let duplicates = 0;
  for (let index = hasHeader ? 1 : 0; index < lines.length; index += 1) {
    const cells = splitCsvRow(lines[index] ?? "", delimiter);
    const email = (cells[emailIndex] ?? "").trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      invalid += 1;
      continue;
    }
    const fields: Record<string, string> = { email };
    headers.forEach((header, cellIndex) => {
      fields[header] = cells[cellIndex]?.trim() ?? "";
    });
    if (unique.has(email)) {
      duplicates += 1;
    }
    unique.set(email, { email, fields });
  }
  return {
    rows: [...unique.values()],
    totalLines: rawLines.length,
    empty,
    invalid,
    duplicates,
  };
}

function normalizePlaceholderKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function personalizeTemplate(value: string, fields: Record<string, string>): string {
  const lookup: Record<string, string> = {};
  for (const [k, v] of Object.entries(fields)) {
    lookup[normalizePlaceholderKey(k)] = v;
  }
  return value.replace(/\{\{\s*([a-zA-Z0-9_äöüÄÖÜß.-]+)\s*\}\}/g, (match, key: string) => {
    const norm = normalizePlaceholderKey(key);
    return Object.prototype.hasOwnProperty.call(lookup, norm) ? lookup[norm] : match;
  });
}

function normalizeRecipientRow(row: {
  id: string;
  email: string;
  fields: unknown;
  position: number;
}): Recipient {
  return {
    ...row,
    fields: (row.fields ?? {}) as Record<string, string>,
  };
}

function CampaignDetailPage() {
  const { campaignId } = Route.useParams();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [recipientPage, setRecipientPage] = useState(0);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [subjectInput, setSubjectInput] = useState("");
  const [senderInput, setSenderInput] = useState("");
  const [htmlDraft, setHtmlDraft] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  const [descriptionDraft, setDescriptionDraft] = useState<string | null>(null);
  const [replyToDraft, setReplyToDraft] = useState<string | null>(null);
  const [subjectRotateDraft, setSubjectRotateDraft] = useState<number | null>(null);
  const [senderRotateDraft, setSenderRotateDraft] = useState<number | null>(null);
  const [sendPauseDraft, setSendPauseDraft] = useState<number | null>(null);
  const [dailyLimitDraft, setDailyLimitDraft] = useState<number | null>(null);
  const [rotationWindowDraft, setRotationWindowDraft] = useState<number | null>(null);
  const [previewFullscreen, setPreviewFullscreen] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState<"idle" | "saving" | "saved" | "error">(
    "idle",
  );
  const [testEmail, setTestEmail] = useState("");
  const [testSmtpAccountId, setTestSmtpAccountId] = useState<string>("");
  const [sendingTest, setSendingTest] = useState(false);
  const getCampaignAlias = useServerFn(getOrCreateCampaignAlias);
  const listSmtp = useServerFn(listSmtpAccounts);
  const sendOne = useServerFn(sendSmtpMail);

  const smtpQuery = useQuery({
    queryKey: ["campaign-detail-smtp-accounts"],
    queryFn: () => listSmtp(),
  });
  const smtpAccounts = smtpQuery.data?.accounts ?? [];

  // Welche SMTP-Konten dürfen für diese Kampagne benutzt werden?
  // Leere Auswahl = alle aktiven Konten (Fallback im Sender).
  const campaignSmtpQuery = useQuery({
    queryKey: ["campaign-smtp-accounts", campaignId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("campaign_smtp_accounts" as never)
        .select("account_id")
        .eq("campaign_id", campaignId);
      if (error) throw error;
      return ((data ?? []) as Array<{ account_id: string }>).map((r) => r.account_id);
    },
  });
  const [smtpSelectionDraft, setSmtpSelectionDraft] = useState<Set<string> | null>(null);
  const currentSmtpSelection = smtpSelectionDraft ?? new Set<string>(campaignSmtpQuery.data ?? []);
  const toggleSmtpAccount = (id: string) => {
    const next = new Set(currentSmtpSelection);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSmtpSelectionDraft(next);
  };
  const saveSmtpSelection = useMutation({
    mutationFn: async () => {
      const selected = Array.from(currentSmtpSelection);
      const { error: delErr } = await supabase
        .from("campaign_smtp_accounts" as never)
        .delete()
        .eq("campaign_id", campaignId);
      if (delErr) throw delErr;
      if (selected.length > 0) {
        const rows = selected.map((account_id) => ({ campaign_id: campaignId, account_id }));
        const { error: insErr } = await (
          supabase as unknown as {
            from: (t: string) => {
              insert: (r: unknown) => Promise<{ error: { message: string } | null }>;
            };
          }
        )
          .from("campaign_smtp_accounts")
          .insert(rows);
        if (insErr) throw insErr;
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["campaign-smtp-accounts", campaignId] });
      setSmtpSelectionDraft(null);
      toast.success("SMTP-Auswahl gespeichert");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Test-Mail-Feld einmalig mit eigener Adresse vorbefüllen
  useEffect(() => {
    if (!testEmail && user?.email) setTestEmail(user.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  // Default SMTP-Konto vorauswählen (is_default → erstes aktives → erstes)
  useEffect(() => {
    if (testSmtpAccountId || !smtpAccounts.length) return;
    const def =
      smtpAccounts.find((a) => (a as { is_default?: boolean }).is_default) ??
      smtpAccounts.find((a) => (a as { is_active?: boolean }).is_active !== false) ??
      smtpAccounts[0];
    if (def) setTestSmtpAccountId(def.id);
  }, [smtpAccounts, testSmtpAccountId]);

  // Warnung beim Verlassen/Neuladen, während Auto-Save läuft
  useBlocker({
    shouldBlockFn: () => autoSaveStatus === "saving",
    enableBeforeUnload: autoSaveStatus === "saving",
  });

  const campaignQuery = useQuery({
    queryKey: ["campaign-detail", campaignId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("campaigns")
        .select("*")
        .eq("id", campaignId)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const recipientsQuery = useQuery({
    queryKey: ["campaign-recipients", campaignId, recipientPage, recipientSearch.trim()],
    queryFn: async () => {
      const from = recipientPage * RECIPIENT_PAGE_SIZE;
      const to = from + RECIPIENT_PAGE_SIZE - 1;
      const search = recipientSearch.trim();
      let query = supabase
        .from("campaign_recipients")
        .select("id,email,fields,position", { count: "exact" })
        .eq("campaign_id", campaignId)
        .order("position")
        .range(from, to);

      if (search) query = query.ilike("email", `%${search}%`);

      const { data, error, count } = await query;
      if (error) throw error;
      return {
        items: (data ?? []).map(normalizeRecipientRow),
        total: count ?? 0,
      };
    },
    placeholderData: (previous) => previous,
  });

  const subjectsQuery = useQuery({
    queryKey: ["campaign-subjects", campaignId],
    queryFn: async () => {
      const PAGE = 1000;
      let from = 0;
      const all: Array<{ id: string; subject: string; position: number }> = [];

      while (true) {
        const { data, error } = await supabase
          .from("campaign_subjects")
          .select("id,subject,position")
          .eq("campaign_id", campaignId)
          .order("position")
          .range(from, from + PAGE - 1);
        if (error) throw error;
        const chunk = data ?? [];
        all.push(...chunk);
        if (chunk.length < PAGE) break;
        from += PAGE;
      }
      return all.map((row) => ({
        id: row.id,
        value: row.subject,
        position: row.position,
      }));
    },
  });

  const sendersQuery = useQuery({
    queryKey: ["campaign-sender-names", campaignId],
    queryFn: async () => {
      const PAGE = 1000;
      let from = 0;
      const all: Array<{ id: string; sender_name: string; position: number }> = [];

      while (true) {
        const { data, error } = await supabase
          .from("campaign_sender_names")
          .select("id,sender_name,position")
          .eq("campaign_id", campaignId)
          .order("position")
          .range(from, from + PAGE - 1);
        if (error) throw error;
        const chunk = data ?? [];
        all.push(...chunk);
        if (chunk.length < PAGE) break;
        from += PAGE;
      }
      return all.map((row) => ({
        id: row.id,
        value: row.sender_name,
        position: row.position,
      }));
    },
  });

  const campaign = campaignQuery.data;
  const aliasQuery = useQuery({
    queryKey: ["campaign-pixel-alias", campaignId],
    queryFn: async () => {
      try {
        return await getCampaignAlias({ data: { campaignId } });
      } catch (error) {
        console.warn("[campaign-detail] alias lookup failed, falling back to direct pixel", error);
        return { shortToken: null };
      }
    },
    enabled: Boolean(campaign && campaign.use_stealth_pixel !== false),
    retry: false,
  });
  const recipients = recipientsQuery.data?.items ?? [];
  const recipientTotal = recipientsQuery.data?.total ?? 0;
  const recipientPageCount = Math.max(1, Math.ceil(recipientTotal / RECIPIENT_PAGE_SIZE));
  const recipientPageStart = recipientTotal ? recipientPage * RECIPIENT_PAGE_SIZE + 1 : 0;
  const recipientPageEnd = Math.min(recipientTotal, (recipientPage + 1) * RECIPIENT_PAGE_SIZE);
  const subjects = subjectsQuery.data ?? [];
  const senders = sendersQuery.data ?? [];
  const trackingOrigin = campaign?.tracking_domain?.trim()
    ? `https://${campaign.tracking_domain.trim()}`
    : DEFAULT_TRACKING_ORIGIN;
  // Merge-Tags werden vom Versand-Tool ersetzt:
  //   s = Absender-Adresse, r = Empfaenger-Adresse
  const pixelQuery = "?s={{sender_email}}&r={{email}}";
  const pixelUrl =
    campaign?.use_stealth_pixel !== false && aliasQuery.data?.shortToken
      ? `${trackingOrigin}/a/${aliasQuery.data.shortToken}.png${pixelQuery}`
      : `${trackingOrigin}/api/public/pixel/${campaignId}.gif${pixelQuery}`;
  const currentHtml = injectTrackingSnippet(htmlDraft ?? campaign?.html_content ?? "", pixelUrl);
  const trackingSnippetLine = currentHtml.includes(TRACKING_SNIPPET_START)
    ? currentHtml.slice(0, currentHtml.indexOf(TRACKING_SNIPPET_START)).split("\n").length
    : null;
  const currentName = nameDraft ?? campaign?.name ?? "";
  const currentDescription = descriptionDraft ?? campaign?.description ?? "";
  const currentReplyTo = replyToDraft ?? campaign?.reply_to ?? "";
  const cAny = (campaign ?? {}) as Record<string, unknown>;
  const currentSubjectRotate =
    subjectRotateDraft ?? (cAny.rotate_subject_every_n as number | undefined) ?? 1;
  const currentSenderRotate =
    senderRotateDraft ?? (cAny.rotate_sender_every_n as number | undefined) ?? 1;
  const currentSendPause = sendPauseDraft ?? (cAny.send_pause_seconds as number | undefined) ?? 30;
  const currentDailyLimit = dailyLimitDraft ?? (cAny.daily_send_limit as number | undefined) ?? 50;
  const currentRotationWindow =
    rotationWindowDraft ?? (cAny.rotation_window_hours as number | undefined) ?? 24;
  const filteredRecipients = recipients;

  useEffect(() => {
    setRecipientPage(0);
  }, [recipientSearch]);

  const refresh = (key: string) => queryClient.invalidateQueries({ queryKey: [key, campaignId] });

  const saveCampaign = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("campaigns")
        .update({
          name: currentName.trim(),
          description: currentDescription.trim() || null,
          reply_to: currentReplyTo.trim().toLowerCase() || null,
          rotate_subject_every_n: Math.max(0, Math.floor(currentSubjectRotate)),
          rotate_sender_every_n: Math.max(0, Math.floor(currentSenderRotate)),
          send_pause_seconds: Math.min(3600, Math.max(0, Math.floor(currentSendPause))),
          daily_send_limit: Math.min(100000, Math.max(1, Math.floor(currentDailyLimit))),
          rotation_window_hours: Math.max(1, Math.floor(currentRotationWindow)),
        })
        .eq("id", campaignId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await refresh("campaign-detail");
      await queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast.success("Kampagneneinstellungen gespeichert");
      setSubjectRotateDraft(null);
      setSenderRotateDraft(null);
      setSendPauseDraft(null);
      setDailyLimitDraft(null);
      setRotationWindowDraft(null);
    },
    onError: (error) => toast.error(error.message),
  });

  const saveHtml = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("campaigns")
        .update({ html_content: currentHtml })
        .eq("id", campaignId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await refresh("campaign-detail");
      toast.success("HTML gespeichert");
    },
    onError: (error) => toast.error(error.message),
  });

  async function sendTestMail() {
    const target = testEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(target)) {
      toast.error("Bitte eine gültige Test-E-Mail eintragen");
      return;
    }
    if (!testSmtpAccountId) {
      toast.error("Bitte ein SMTP-Konto auswählen");
      return;
    }
    if (!recipientTotal) {
      toast.error("Keine Empfänger vorhanden – bitte zuerst eine CSV importieren");
      return;
    }
    if (!currentHtml.trim()) {
      toast.error("HTML-Inhalt ist leer");
      return;
    }
    // Random Empfänger direkt aus der Datenbank ziehen, ohne die komplette Liste in den Browser zu laden.
    const randomOffset = Math.floor(Math.random() * recipientTotal);
    const { data: sampleData, error: sampleError } = await supabase
      .from("campaign_recipients")
      .select("id,email,fields,position")
      .eq("campaign_id", campaignId)
      .order("position")
      .range(randomOffset, randomOffset)
      .maybeSingle();
    if (sampleError || !sampleData) {
      toast.error(sampleError?.message ?? "Zufälliger Empfänger konnte nicht geladen werden");
      return;
    }
    const sample = normalizeRecipientRow(sampleData);
    const subjectTemplate =
      subjects[Math.floor(Math.random() * Math.max(subjects.length, 1))]?.value ||
      currentName ||
      "Kampagnen-Test";
    const personalizedSubject = personalizeTemplate(subjectTemplate, sample.fields);
    const personalizedHtml = personalizeTemplate(currentHtml, sample.fields);
    setSendingTest(true);
    try {
      const res = await sendOne({
        data: {
          account_id: testSmtpAccountId,
          to: target,
          subject: `[TEST] ${personalizedSubject}`,
          html: personalizedHtml,
          reply_to: currentReplyTo.trim() || undefined,
          campaign_id: null,
        } as never,
      });
      if (!(res as { ok?: boolean })?.ok) {
        throw new Error((res as { error?: string })?.error ?? "Test-Mail fehlgeschlagen");
      }
      toast.success(
        `Test-Mail an ${target} gesendet · Platzhalter aus Empfänger "${sample.email}"`,
        { duration: 7000 },
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Test-Mail fehlgeschlagen");
    } finally {
      setSendingTest(false);
    }
  }

  const autoSave = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("campaigns")
        .update({
          name: currentName.trim(),
          description: currentDescription.trim() || null,
          html_content: currentHtml,
          reply_to: currentReplyTo.trim().toLowerCase() || null,
        })
        .eq("id", campaignId);
      if (error) throw error;
    },
    onMutate: () => setAutoSaveStatus("saving"),
    onSuccess: async () => {
      setAutoSaveStatus("saved");
      await queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: () => setAutoSaveStatus("error"),
  });

  useEffect(() => {
    const hasChanges =
      nameDraft !== null ||
      descriptionDraft !== null ||
      htmlDraft !== null ||
      replyToDraft !== null;
    if (!hasChanges || !currentName.trim()) return;
    setAutoSaveStatus("idle");
    const timer = setTimeout(() => autoSave.mutate(), 1500);
    return () => clearTimeout(timer);
  }, [nameDraft, descriptionDraft, htmlDraft, replyToDraft]);

  async function addRecipient() {
    const email = recipientEmail.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || !user) {
      toast.error("Bitte eine gültige E-Mail-Adresse eingeben");
      return;
    }
    const { error } = await supabase.from("campaign_recipients").upsert(
      {
        campaign_id: campaignId,
        user_id: user.id,
        email,
        fields: { email },
        position: recipientTotal,
      },
      { onConflict: "campaign_id,email" },
    );
    if (error) return toast.error(error.message);
    setRecipientEmail("");
    await refresh("campaign-recipients");
    toast.success("Empfänger hinzugefügt");
  }

  async function importRecipients(file: File) {
    if (!user) return;
    const parsed = parseRecipientsCsv(await file.text());
    const { rows: parsedRows, empty, invalid, duplicates } = parsed;
    if (!parsedRows.length) {
      return toast.error(`Keine gültigen Empfänger gefunden (${invalid} ungültig, ${empty} leer)`);
    }
    if (parsedRows.length > MAX_RECIPIENTS_PER_CSV) {
      return toast.error(
        `Maximal ${MAX_RECIPIENTS_PER_CSV.toLocaleString("de-DE")} Empfänger pro CSV-Upload erlaubt (Datei enthält ${parsedRows.length.toLocaleString("de-DE")}).`,
      );
    }
    const rows = parsedRows.map((recipient, index) => ({
      campaign_id: campaignId,
      user_id: user.id,
      email: recipient.email,
      fields: recipient.fields,
      position: index,
    }));
    const BATCH = 500;
    let inserted = 0;
    const tId = toast.loading(`Importiere 0 / ${rows.length} Empfänger…`);
    for (let i = 0; i < rows.length; i += BATCH) {
      const slice = rows.slice(i, i + BATCH);
      const { error } = await supabase
        .from("campaign_recipients")
        .upsert(slice, { onConflict: "campaign_id,email" });
      if (error) {
        toast.error(`Fehler ab Zeile ${i + 1}: ${error.message}`, { id: tId });
        await refresh("campaign-recipients");
        return;
      }
      inserted += slice.length;
      toast.loading(`Importiere ${inserted} / ${rows.length} Empfänger…`, { id: tId });
    }
    await refresh("campaign-recipients");
    toast.success(
      `${inserted} importiert · ${duplicates} Duplikate · ${invalid} ungültig · ${empty} leer`,
      { id: tId, duration: 8000 },
    );
  }

  async function removeRecipient(id: string) {
    const { error } = await supabase.from("campaign_recipients").delete().eq("id", id);
    if (error) return toast.error(error.message);
    await refresh("campaign-recipients");
  }

  async function addListItem(type: "subject" | "sender") {
    if (!user) return;
    const value = (type === "subject" ? subjectInput : senderInput).trim();
    if (!value) return;
    if (type === "subject" && subjects.length >= MAX_SUBJECTS) {
      toast.error(`Maximal ${MAX_SUBJECTS} Betreffzeilen pro Kampagne erlaubt`);
      return;
    }
    if (type === "sender" && senders.length >= MAX_SENDERS) {
      toast.error(`Maximal ${MAX_SENDERS} Absendernamen pro Kampagne erlaubt`);
      return;
    }
    const { error } =
      type === "subject"
        ? await supabase.from("campaign_subjects").insert({
            campaign_id: campaignId,
            user_id: user.id,
            subject: value,
            position: subjects.length,
          })
        : await supabase.from("campaign_sender_names").insert({
            campaign_id: campaignId,
            user_id: user.id,
            sender_name: value,
            position: senders.length,
          });
    if (error) return toast.error(error.message);
    if (type === "subject") {
      setSubjectInput("");
    } else {
      setSenderInput("");
    }
    await refresh(type === "subject" ? "campaign-subjects" : "campaign-sender-names");
    toast.success(type === "subject" ? "Betreff hinzugefügt" : "Absendername hinzugefügt");
  }

  async function removeListItem(type: "subject" | "sender", id: string) {
    const table = type === "subject" ? "campaign_subjects" : "campaign_sender_names";
    const { error } = await supabase.from(table).delete().eq("id", id);
    if (error) return toast.error(error.message);
    await refresh(type === "subject" ? "campaign-subjects" : "campaign-sender-names");
  }

  async function removeListItems(type: "subject" | "sender", ids: string[]) {
    if (!ids.length) return;
    const table = type === "subject" ? "campaign_subjects" : "campaign_sender_names";
    const { error } = await supabase.from(table).delete().in("id", ids);
    if (error) return toast.error(error.message);
    await refresh(type === "subject" ? "campaign-subjects" : "campaign-sender-names");
    toast.success(`${ids.length} Einträge gelöscht`);
  }

  async function removeAllListItems(type: "subject" | "sender") {
    const table = type === "subject" ? "campaign_subjects" : "campaign_sender_names";
    const { error } = await supabase.from(table).delete().eq("campaign_id", campaignId);
    if (error) return toast.error(error.message);
    await refresh(type === "subject" ? "campaign-subjects" : "campaign-sender-names");
    toast.success("Alle Einträge gelöscht");
  }

  async function importListItems(type: "subject" | "sender", file: File) {
    if (!user) return;
    const text = await file.text();
    const values = Array.from(
      new Set(
        text
          .split(/\r?\n/)
          .map((line) => {
            if (type === "subject") {
              // Betreff = ganze Zeile (kann Kommas/Semikolons enthalten),
              // nur umschließende Quotes entfernen.
              return line
                .trim()
                .replace(/^"(.*)"$/, "$1")
                .trim();
            }
            const delim = line.includes(";") ? ";" : ",";
            const cells = splitCsvRow(line, delim).map((c) => c.replace(/^"|"$/g, "").trim());
            if (cells.length >= 2) {
              // CSV-Schema: Vorname, Nachname, Firma -> "Firma - Vorname Nachname"
              const first = cells[0] ?? "";
              const last = cells[1] ?? "";
              const company = cells[2] ?? "";
              const person = [first, last].filter(Boolean).join(" ").trim();
              if (company && person) return `${company} - ${person}`;
              return company || person || first;
            }
            return cells[0] ?? "";
          })
          .map((value) => value.trim())
          .filter((value) => value.length > 0 && value.length <= 500),
      ),
    );
    if (!values.length) return toast.error("Keine gültigen Einträge gefunden");
    if (type === "subject") {
      const existing = new Set(subjects.map((item) => item.value));
      const fresh = values.filter((value) => !existing.has(value));
      if (!fresh.length) return toast.info("Alle Einträge sind bereits vorhanden");
      const available = MAX_SUBJECTS - subjects.length;
      if (available <= 0) {
        return toast.error(`Maximal ${MAX_SUBJECTS} Betreffzeilen pro Kampagne erlaubt`);
      }
      const toImport = fresh.slice(0, available);
      const rows = toImport.map((subject, index) => ({
        campaign_id: campaignId,
        user_id: user.id,
        subject,
        position: subjects.length + index,
      }));
      const BATCH = 200;
      let inserted = 0;
      const tId = toast.loading(`Importiere 0 / ${rows.length} Betreffe…`);
      for (let i = 0; i < rows.length; i += BATCH) {
        const slice = rows.slice(i, i + BATCH);
        const { error } = await supabase.from("campaign_subjects").insert(slice);
        if (error) {
          toast.error(`Fehler bei Zeile ${i + 1}: ${error.message}`, { id: tId });
          break;
        }
        inserted += slice.length;
        toast.loading(`Importiere ${inserted} / ${rows.length} Betreffe…`, { id: tId });
      }
      await refresh("campaign-subjects");
      toast.success(
        toImport.length < fresh.length
          ? `${inserted} Betreffzeilen importiert (Limit ${MAX_SUBJECTS} erreicht)`
          : `${inserted} Betreffzeilen importiert`,
        { id: tId },
      );
    } else {
      const existing = new Set(senders.map((item) => item.value));
      const fresh = values.filter((value) => !existing.has(value));
      if (!fresh.length) return toast.info("Alle Einträge sind bereits vorhanden");
      const available = MAX_SENDERS - senders.length;
      if (available <= 0) {
        return toast.error(`Maximal ${MAX_SENDERS} Absendernamen pro Kampagne erlaubt`);
      }
      const toImport = fresh.slice(0, available);
      const rows = toImport.map((sender_name, index) => ({
        campaign_id: campaignId,
        user_id: user.id,
        sender_name,
        position: senders.length + index,
      }));
      const BATCH = 200;
      let inserted = 0;
      const tId = toast.loading(`Importiere 0 / ${rows.length} Absender…`);
      for (let i = 0; i < rows.length; i += BATCH) {
        const slice = rows.slice(i, i + BATCH);
        const { error } = await supabase.from("campaign_sender_names").insert(slice);
        if (error) {
          toast.error(`Fehler bei Zeile ${i + 1}: ${error.message}`, { id: tId });
          break;
        }
        inserted += slice.length;
        toast.loading(`Importiere ${inserted} / ${rows.length} Absender…`, { id: tId });
      }
      await refresh("campaign-sender-names");
      if (toImport.length < fresh.length) {
        toast.success(`${inserted} Absendernamen importiert (Limit ${MAX_SENDERS} erreicht)`, {
          id: tId,
        });
      } else {
        toast.success(`${inserted} Absendernamen importiert`, { id: tId });
      }
    }
  }

  if (campaignQuery.isLoading) {
    return (
      <div className="grid min-h-[60vh] place-items-center">
        <Loader2 className="size-7 animate-spin text-primary" />
      </div>
    );
  }

  if (!campaign) return <CampaignDetailNotFound />;

  const openRate = campaign.sent_count
    ? Math.round((campaign.open_count / campaign.sent_count) * 100)
    : 0;

  return (
    <div className="min-h-full bg-muted/10">
      <header className="border-b bg-background px-6 py-5 lg:px-8">
        <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/campaigns" className="hover:text-foreground">
            Kampagnen
          </Link>
          <span>/</span>
          <span className="text-foreground">{campaign.name}</span>
        </div>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold tracking-tight">{campaign.name}</h1>
              <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                {campaign.status}
              </span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Alle Inhalte und Listen dieser Kampagne zentral bearbeiten.
            </p>
            <p
              className={
                autoSaveStatus === "error"
                  ? "mt-2 text-xs text-destructive"
                  : "mt-2 text-xs text-muted-foreground"
              }
              role="status"
            >
              {autoSaveStatus === "saving" && "Änderungen werden automatisch gespeichert …"}
              {autoSaveStatus === "saved" && "Alle Änderungen automatisch gespeichert"}
              {autoSaveStatus === "error" && "Auto-Save fehlgeschlagen – bitte erneut versuchen"}
              {autoSaveStatus === "idle" &&
                (nameDraft !== null ||
                  descriptionDraft !== null ||
                  htmlDraft !== null ||
                  replyToDraft !== null) &&
                "Ungespeicherte Änderungen"}
            </p>
          </div>
          <Button asChild variant="outline">
            <Link to="/campaigns">
              <ArrowLeft className="mr-2 size-4" />
              Zurück zu Kampagnen
            </Link>
          </Button>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
          <Metric label="Empfänger" value={recipientTotal} />
          <Metric label="Gesendet" value={campaign.sent_count} />
          <Metric label="Öffnungen" value={`${campaign.open_count} · ${openRate}%`} />
          <Metric label="Bounces" value={campaign.bounce_count} />
        </div>
      </header>

      <Tabs defaultValue="recipients" className="px-6 py-6 lg:px-8">
        <TabsList className="h-auto w-full justify-start overflow-x-auto border bg-background p-1">
          <TabsTrigger value="recipients">
            <Users className="mr-2 size-4" />
            Empfänger <Count value={recipientTotal} />
          </TabsTrigger>
          <TabsTrigger value="subjects">
            <Mail className="mr-2 size-4" />
            Betreffzeilen <Count value={subjects.length} />
          </TabsTrigger>
          <TabsTrigger value="senders">
            <UserRound className="mr-2 size-4" />
            Absendernamen <Count value={senders.length} />
          </TabsTrigger>
          <TabsTrigger value="html">
            <Code2 className="mr-2 size-4" />
            HTML & Vorschau
          </TabsTrigger>
          <TabsTrigger value="settings">Einstellungen</TabsTrigger>
        </TabsList>

        <TabsContent value="recipients" className="mt-5 space-y-4">
          <SectionHeading
            title="Empfängerliste"
            description="Diese Empfänger gehören ausschließlich zu dieser Kampagne."
          />
          <div className="flex flex-col gap-3 rounded-lg border bg-background p-4 md:flex-row md:items-center">
            <div className="relative min-w-0 flex-1">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={recipientSearch}
                onChange={(event) => setRecipientSearch(event.target.value)}
                placeholder="Nach E-Mail suchen …"
                className="pl-9"
              />
            </div>
            <Input
              value={recipientEmail}
              onChange={(event) => setRecipientEmail(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void addRecipient();
              }}
              placeholder="empfaenger@beispiel.de"
              className="md:w-72"
            />
            <Button onClick={() => void addRecipient()}>
              <Plus className="mr-2 size-4" />
              Hinzufügen
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void importRecipients(file);
                event.target.value = "";
              }}
            />
            <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
              <Upload className="mr-2 size-4" />
              CSV importieren
            </Button>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
            <p>
              Maximal {MAX_RECIPIENTS_PER_CSV.toLocaleString("de-DE")} Empfänger pro CSV-Upload.
            </p>
            <p>
              {recipientTotal.toLocaleString("de-DE")} Empfänger · Anzeige {recipientPageStart}–
              {recipientPageEnd}
            </p>
          </div>
          <div className="overflow-hidden rounded-lg border bg-background">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">#</TableHead>
                  <TableHead>E-Mail</TableHead>
                  <TableHead>Platzhalter</TableHead>
                  <TableHead className="w-20 text-right">Aktion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRecipients.map((recipient, index) => (
                  <TableRow key={recipient.id}>
                    <TableCell className="text-muted-foreground">
                      {recipientPage * RECIPIENT_PAGE_SIZE + index + 1}
                    </TableCell>
                    <TableCell className="font-medium">{recipient.email}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(() => {
                          const PRIORITY = [
                            "anrede",
                            "formel",
                            "vorname",
                            "name",
                            "strasse",
                            "plz",
                            "ort",
                            "geb",
                          ];
                          const entries = Object.entries(recipient.fields).filter(
                            ([key]) => key !== "email",
                          );
                          entries.sort(([a], [b]) => {
                            const ia = PRIORITY.indexOf(a);
                            const ib = PRIORITY.indexOf(b);
                            if (ia === -1 && ib === -1) return a.localeCompare(b);
                            if (ia === -1) return 1;
                            if (ib === -1) return -1;
                            return ia - ib;
                          });
                          return entries.slice(0, 8);
                        })().map(([key, value]) => (
                          <span
                            key={key}
                            className="rounded bg-muted px-2 py-1 text-xs text-muted-foreground"
                          >
                            {key}: {value || "—"}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="icon"
                        variant="ghost"
                        title="Empfänger entfernen"
                        onClick={() => void removeRecipient(recipient.id)}
                      >
                        <Trash2 className="size-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!filteredRecipients.length && (
                  <TableRow>
                    <TableCell colSpan={4} className="h-32 text-center text-muted-foreground">
                      Noch keine passenden Empfänger vorhanden.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRecipientPage((page) => Math.max(0, page - 1))}
              disabled={recipientPage === 0 || recipientsQuery.isFetching}
            >
              <ChevronLeft className="mr-1 size-4" />
              Zurück
            </Button>
            <span className="min-w-24 text-center text-xs text-muted-foreground">
              Seite {recipientPage + 1} / {recipientPageCount}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRecipientPage((page) => Math.min(recipientPageCount - 1, page + 1))}
              disabled={recipientPage >= recipientPageCount - 1 || recipientsQuery.isFetching}
            >
              Weiter
              <ChevronRight className="ml-1 size-4" />
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="subjects" className="mt-5 space-y-4">
          <SectionHeading
            title="Betreffzeilen"
            description="Die Reihenfolge wird bei der Rotation berücksichtigt."
          />
          <p className="text-xs text-muted-foreground">
            {subjects.length} / {MAX_SUBJECTS} Betreffzeilen
          </p>
          <EditableList
            input={subjectInput}
            setInput={setSubjectInput}
            placeholder="Neue Betreffzeile"
            buttonLabel="Betreff hinzufügen"
            items={subjects}
            onAdd={() => void addListItem("subject")}
            onRemove={(id) => void removeListItem("subject", id)}
            onRemoveMany={(ids) => void removeListItems("subject", ids)}
            onRemoveAll={() => void removeAllListItems("subject")}
            emptyText="Noch keine Betreffzeilen angelegt."
            onImport={(file) => void importListItems("subject", file)}
            importLabel="Betreffe als CSV importieren"
            importHint={`Eine Betreffzeile pro Zeile (CSV/TXT). Maximal ${MAX_SUBJECTS} pro Kampagne.`}
          />
        </TabsContent>

        <TabsContent value="senders" className="mt-5 space-y-4">
          <SectionHeading
            title="Absendernamen"
            description="Lege mehrere Namen für die Absender-Rotation dieser Kampagne fest."
          />
          <p className="text-xs text-muted-foreground">
            {senders.length} / {MAX_SENDERS} Absendernamen
          </p>
          <EditableList
            input={senderInput}
            setInput={setSenderInput}
            placeholder="Zum Beispiel: Anna von EasyKredit"
            buttonLabel="Absender hinzufügen"
            items={senders}
            onAdd={() => void addListItem("sender")}
            onRemove={(id) => void removeListItem("sender", id)}
            onRemoveMany={(ids) => void removeListItems("sender", ids)}
            onRemoveAll={() => void removeAllListItems("sender")}
            emptyText="Noch keine Absendernamen angelegt."
            onImport={(file) => void importListItems("sender", file)}
            importLabel="Absender als CSV importieren"
            importHint={`Ein Absendername pro Zeile (CSV/TXT). Maximal ${MAX_SENDERS} pro Kampagne.`}
          />
        </TabsContent>

        <TabsContent value="html" className="mt-5 space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <SectionHeading
              title="HTML-Editor & Vorschau"
              description="Änderungen gelten nur für diese Kampagne."
            />
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex flex-col gap-1">
                <Label htmlFor="test-mail-to" className="text-xs text-muted-foreground">
                  Test-Mail an
                </Label>
                <Input
                  id="test-mail-to"
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="du@beispiel.de"
                  className="h-9 w-56"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground">SMTP-Konto</Label>
                <Select value={testSmtpAccountId} onValueChange={setTestSmtpAccountId}>
                  <SelectTrigger className="h-9 w-52">
                    <SelectValue placeholder="SMTP wählen…" />
                  </SelectTrigger>
                  <SelectContent>
                    {smtpAccounts.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {(a as { label?: string }).label || a.from_email}
                      </SelectItem>
                    ))}
                    {!smtpAccounts.length && (
                      <SelectItem value="__none__" disabled>
                        Keine SMTP-Konten
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                onClick={() => void sendTestMail()}
                disabled={sendingTest || !recipientTotal}
                title={
                  !recipientTotal
                    ? "Bitte zuerst Empfänger importieren"
                    : "Test-Mail mit zufälligen Empfänger-Daten als Platzhalter"
                }
              >
                {sendingTest ? (
                  <Loader2 className="mr-2 size-4 animate-spin" />
                ) : (
                  <Send className="mr-2 size-4" />
                )}
                Test-Mail senden
              </Button>
              <Button onClick={() => saveHtml.mutate()} disabled={saveHtml.isPending}>
                <Save className="mr-2 size-4" />
                HTML speichern
              </Button>
            </div>
          </div>
          {recipientTotal > 0 && (
            <p className="text-xs text-muted-foreground">
              Platzhalter ({"{{vorname}}"}, {"{{name}}"} …) werden aus einem zufälligen Empfänger (
              {recipientTotal.toLocaleString("de-DE")} verfügbar) befüllt.
            </p>
          )}
          <div className="grid min-h-[650px] overflow-hidden rounded-lg border bg-background lg:grid-cols-2">
            <div className="flex min-h-[520px] flex-col border-b lg:border-b-0 lg:border-r">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3 text-sm font-medium">
                <span className="flex items-center gap-2">
                  <Code2 className="size-4 text-primary" />
                  HTML-Code
                </span>
                {trackingSnippetLine && (
                  <span className="rounded-full bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary">
                    Tracking-Snippet · Zeile {trackingSnippetLine}
                  </span>
                )}
              </div>
              <Textarea
                value={currentHtml}
                onChange={(event) =>
                  setHtmlDraft(injectTrackingSnippet(event.target.value, pixelUrl))
                }
                className="min-h-0 flex-1 resize-none rounded-none border-0 font-mono text-xs focus-visible:ring-0"
                spellCheck={false}
              />
            </div>
            <div
              className={
                previewFullscreen
                  ? "fixed inset-y-0 left-60 right-0 z-50 flex flex-col bg-background"
                  : "flex min-h-[520px] flex-col"
              }
            >
              <div className="flex items-center justify-between border-b px-4 py-3">
                <span className="flex items-center gap-2 text-sm font-medium">
                  <Eye className="size-4 text-primary" />
                  Live-Vorschau
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPreviewFullscreen((value) => !value)}
                >
                  {previewFullscreen ? (
                    <Minimize className="mr-2 size-4" />
                  ) : (
                    <Maximize className="mr-2 size-4" />
                  )}
                  {previewFullscreen ? "Schließen" : "Vollbild"}
                </Button>
              </div>
              <div className="min-h-0 flex-1 overflow-auto bg-muted/30 p-5">
                <iframe
                  title="Kampagnenvorschau"
                  srcDoc={currentHtml}
                  sandbox=""
                  className="mx-auto h-full min-h-[560px] w-full max-w-4xl rounded-md border bg-background shadow-sm"
                />
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="settings" className="mt-5 space-y-4">
          <SectionHeading
            title="Kampagneneinstellungen"
            description="Name, Beschreibung und feste Antwortadresse der Kampagne bearbeiten."
          />
          <div className="max-w-2xl space-y-5 rounded-lg border bg-background p-6">
            <div className="space-y-2">
              <Label htmlFor="campaign-name">Kampagnenname</Label>
              <Input
                id="campaign-name"
                value={currentName}
                onChange={(event) => setNameDraft(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="campaign-description">Beschreibung</Label>
              <Textarea
                id="campaign-description"
                value={currentDescription}
                onChange={(event) => setDescriptionDraft(event.target.value)}
                rows={4}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="campaign-reply-to">Feste Reply-To-Adresse</Label>
              <Input
                id="campaign-reply-to"
                type="email"
                value={currentReplyTo}
                onChange={(event) => setReplyToDraft(event.target.value)}
                placeholder="antworten@deine-domain.de"
              />
              <p className="text-xs leading-relaxed text-muted-foreground">
                Alle Antworten dieser Kampagne gehen an diese Adresse – auch wenn du Absender oder
                Domains rotierst. Leer lassen, um die Reply-To-Adresse des SMTP-Kontos zu verwenden.
              </p>
            </div>
            <Button
              onClick={() => saveCampaign.mutate()}
              disabled={!currentName.trim() || saveCampaign.isPending}
            >
              <Save className="mr-2 size-4" />
              Änderungen speichern
            </Button>
          </div>

          <SectionHeading
            title="Rotation & Versand-Takt"
            description="Steuere, wie oft Betreff und Absendername wechseln und in welchem Tempo Mails versendet werden."
          />

          <SectionHeading
            title="SMTP-Konten für diese Kampagne"
            description="Wähle, welche SMTP-Konten beim Versand zufällig benutzt werden dürfen. Keine Auswahl = alle aktiven Konten."
          />
          <div className="max-w-2xl space-y-3 rounded-lg border bg-background p-6">
            {smtpAccounts.length === 0 ? (
              <p className="text-sm text-muted-foreground">Noch keine SMTP-Konten hinterlegt.</p>
            ) : (
              <div className="max-h-80 space-y-2 overflow-y-auto rounded-md border bg-muted/10 p-2">
                {smtpAccounts.map((acc) => {
                  const a = acc as {
                    id: string;
                    from_email?: string;
                    smtp_host?: string;
                    smtp_port?: number;
                    is_active?: boolean;
                  };
                  return (
                    <label
                      key={a.id}
                      className="flex items-center gap-3 rounded-md border bg-background p-3 hover:bg-muted/30"
                    >
                      <Checkbox
                        checked={currentSmtpSelection.has(a.id)}
                        onCheckedChange={() => toggleSmtpAccount(a.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">
                          {a.from_email ?? "(ohne Absender)"}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {a.smtp_host}:{a.smtp_port}
                          {a.is_active === false ? " · inaktiv" : ""}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {currentSmtpSelection.size === 0
                ? "Keine Auswahl — es werden alle aktiven SMTP-Konten benutzt."
                : `${currentSmtpSelection.size} Konto/Konten ausgewählt.`}
            </p>
            <Button
              onClick={() => saveSmtpSelection.mutate()}
              disabled={saveSmtpSelection.isPending || smtpSelectionDraft === null}
            >
              <Save className="mr-2 size-4" />
              SMTP-Auswahl speichern
            </Button>
          </div>

          <div className="max-w-2xl space-y-5 rounded-lg border bg-background p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="rotate-subject">Betreff wechseln alle N Mails</Label>
                <Input
                  id="rotate-subject"
                  type="number"
                  min={0}
                  step={1}
                  value={currentSubjectRotate}
                  onChange={(e) => setSubjectRotateDraft(Number(e.target.value))}
                />
                <p className="text-xs text-muted-foreground">
                  1 = bei jeder Mail neuer Betreff (zufällig gewichtet). 5 = alle 5 Mails wechseln.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="rotate-sender">Absendernamen wechseln alle N Mails</Label>
                <Input
                  id="rotate-sender"
                  type="number"
                  min={0}
                  step={1}
                  value={currentSenderRotate}
                  onChange={(e) => setSenderRotateDraft(Number(e.target.value))}
                />
                <p className="text-xs text-muted-foreground">
                  1 = bei jeder Mail neuer Absender. 10 = alle 10 Mails wechseln.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="send-pause">Pause zwischen Mails (Sekunden)</Label>
                <Input
                  id="send-pause"
                  type="number"
                  min={0}
                  max={3600}
                  step={1}
                  value={currentSendPause}
                  onChange={(e) => setSendPauseDraft(Number(e.target.value))}
                />
                <p className="text-xs text-muted-foreground">
                  Wartezeit zwischen zwei Sendungen. 0 = so schnell wie möglich.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="daily-limit">Maximale Mails pro Tag</Label>
                <Input
                  id="daily-limit"
                  type="number"
                  min={1}
                  max={100000}
                  step={1}
                  value={currentDailyLimit}
                  onChange={(e) => setDailyLimitDraft(Number(e.target.value))}
                />
                <p className="text-xs text-muted-foreground">
                  Sicherheitslimit pro SMTP-Konto und Tag.
                </p>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="rotation-window">Rotationsfenster (Stunden)</Label>
                <Input
                  id="rotation-window"
                  type="number"
                  min={1}
                  step={1}
                  value={currentRotationWindow}
                  onChange={(e) => setRotationWindowDraft(Number(e.target.value))}
                />
                <p className="text-xs text-muted-foreground">
                  Zeitfenster, in dem max_per_window von Betreffen/Absendern geprüft wird.
                </p>
              </div>
            </div>
            <Button onClick={() => saveCampaign.mutate()} disabled={saveCampaign.isPending}>
              <Save className="mr-2 size-4" />
              Rotationseinstellungen speichern
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border bg-muted/20 px-4 py-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
    </div>
  );
}

function Count({ value }: { value: number }) {
  return (
    <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground">
      {value}
    </span>
  );
}

function SectionHeading({ title, description }: { title: string; description: string }) {
  return (
    <div>
      <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
      <p className="mt-1 text-sm text-muted-foreground">{description}</p>
    </div>
  );
}

function EditableList({
  input,
  setInput,
  placeholder,
  buttonLabel,
  items,
  onAdd,
  onRemove,
  onRemoveMany,
  onRemoveAll,
  emptyText,
  onImport,
  importLabel,
  importHint,
}: {
  input: string;
  setInput: (value: string) => void;
  placeholder: string;
  buttonLabel: string;
  items: ListItem[];
  onAdd: () => void;
  onRemove: (id: string) => void;
  onRemoveMany?: (ids: string[]) => void;
  onRemoveAll?: () => void;
  emptyText: string;
  onImport?: (file: File) => void;
  importLabel?: string;
  importHint?: string;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Auswahl auf existierende Items begrenzen
    setSelected((prev) => {
      const ids = new Set(items.map((i) => i.id));
      const next = new Set<string>();
      prev.forEach((id) => ids.has(id) && next.add(id));
      return next;
    });
  }, [items]);

  const allSelected = items.length > 0 && selected.size === items.length;
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.id)));
  };
  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="max-w-4xl space-y-4">
      <div className="flex gap-3 rounded-lg border bg-background p-4">
        <Input
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") onAdd();
          }}
          placeholder={placeholder}
        />
        <Button onClick={onAdd}>
          <Plus className="mr-2 size-4" />
          {buttonLabel}
        </Button>
      </div>
      {onImport && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-dashed bg-muted/30 p-4">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) onImport(file);
              event.target.value = "";
            }}
          />
          <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
            <Upload className="mr-2 size-4" />
            {importLabel ?? "CSV importieren"}
          </Button>
          {importHint && <span className="text-xs text-muted-foreground">{importHint}</span>}
        </div>
      )}
      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-background px-4 py-2">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
            Alle auswählen
          </label>
          <span className="text-xs text-muted-foreground">{selected.size} ausgewählt</span>
          <div className="ml-auto flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={!selected.size || !onRemoveMany}
              onClick={() => {
                if (!onRemoveMany || !selected.size) return;
                if (!confirm(`${selected.size} Einträge löschen?`)) return;
                onRemoveMany(Array.from(selected));
                setSelected(new Set());
              }}
            >
              <Trash2 className="mr-2 size-4" />
              Auswahl löschen
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={!onRemoveAll}
              onClick={() => {
                if (!onRemoveAll) return;
                if (!confirm(`Wirklich alle ${items.length} Einträge löschen?`)) return;
                onRemoveAll();
                setSelected(new Set());
              }}
            >
              <Trash2 className="mr-2 size-4" />
              Alle löschen
            </Button>
          </div>
        </div>
      )}
      <div className="overflow-hidden rounded-lg border bg-background">
        {items.map((item, index) => (
          <div key={item.id} className="flex items-center gap-4 border-b px-4 py-3 last:border-b-0">
            <Checkbox checked={selected.has(item.id)} onCheckedChange={() => toggleOne(item.id)} />
            <span className="grid size-7 shrink-0 place-items-center rounded-full bg-primary/10 text-xs font-medium text-primary">
              {index + 1}
            </span>
            <span className="min-w-0 flex-1 text-sm font-medium">{item.value}</span>
            <Button
              size="icon"
              variant="ghost"
              title="Eintrag entfernen"
              onClick={() => onRemove(item.id)}
            >
              <Trash2 className="size-4 text-destructive" />
            </Button>
          </div>
        ))}
        {!items.length && (
          <div className="py-16 text-center text-sm text-muted-foreground">{emptyText}</div>
        )}
      </div>
    </div>
  );
}

function CampaignDetailError({ error, reset }: { error: Error; reset: () => void }) {
  const router = useRouter();
  return (
    <div className="grid min-h-[70vh] place-items-center p-8">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold">Kampagne konnte nicht geladen werden</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
        <Button
          className="mt-5"
          onClick={() => {
            router.invalidate();
            reset();
          }}
        >
          Erneut versuchen
        </Button>
      </div>
    </div>
  );
}

function CampaignDetailNotFound() {
  return (
    <div className="grid min-h-[70vh] place-items-center p-8">
      <div className="text-center">
        <h1 className="text-xl font-semibold">Kampagne nicht gefunden</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Die Kampagne existiert nicht oder du hast keinen Zugriff.
        </p>
        <Button asChild className="mt-5">
          <Link to="/campaigns">Zur Kampagnenübersicht</Link>
        </Button>
      </div>
    </div>
  );
}
